import * as React from 'react';
import PropTypes from 'prop-types';
import {Form} from 'react-final-form';
import axios from 'axios';
import finalFormErrorHandler from '../../../helper/form/finalFormErrorHandler';
import objectToFormData from '../../../helper/form/objectToFormData';
import {submitRequest} from './submitRequest';
import {isEqual} from 'lodash';

let abortController;
const cancelTokenErrorMessage = "Operation canceled due to new request.";
const FormManager = ({ url, onError, onInvalid, onSuccess, multipart, method, render, modifyAxiosArgs, ...props }) => {
  async function submit(data) {
    let response;
    const config = {
      headers: []
    };

    if (multipart) {
      data = objectToFormData(data)
      config.headers['Content-Type'] = 'multipart/form-data';
    }

    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    const axiosArgs = modifyAxiosArgs({
      method,
      url,
      data,
      config,
      signal: abortController.signal,
    });

    try {
      response = await submitRequest(axiosArgs);
    } catch (error) {
      if (axios.isCancel(error)) {
        return false;
      }

      return finalFormErrorHandler(error, onError, onInvalid);
    }

    onSuccess(response);

    return true;
  }

  return <Form {...props} onSubmit={submit} render={(props) => {
    return render({ ...props, abortController })
  }}/>;
};

FormManager.propTypes = {
  method: PropTypes.oneOf(['post', 'put', 'patch']),
  url: PropTypes.string.isRequired,
  render: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  modifyAxiosArgs: PropTypes.func,
  initialValues: PropTypes.object,
  mutators: PropTypes.object,
  decorators: PropTypes.arrayOf(PropTypes.func),
};

FormManager.defaultProps = {
  method: 'post',
  onError: error => {
    // eslint-disable-next-line no-alert
    window.alert(error.response.data.message ?  error.response.data.message : 'Er is een onbekende fout opgetreden');
    throw error;
  },
  onSuccess: () => {},
  multipart: false,
  modifyAxiosArgs: args => args,
};

const MemoizedFormManager = React.memo(FormManager, (prevProps, nextProps) => {
  const invalidChanges = [];

  if (prevProps.render !== nextProps.render) {
    invalidChanges.push('render');
  }

  if (prevProps.initialValues !== nextProps.initialValues) {
    invalidChanges.push('initialValues');
  }

  if (prevProps.modifyAxiosArgs !== nextProps.modifyAxiosArgs) {
    invalidChanges.push('modifyAxiosArgs');
  }

  if (prevProps.decorators !== nextProps.decorators) {
    invalidChanges.push('decorators');
  }

  if (prevProps.mutators !== nextProps.mutators) {
    invalidChanges.push('mutators');
  }

  if (process.env.NODE_ENV === 'development') {
    invalidChanges.forEach(change => console.warn(`FormManager prop ${change} changed during re-render`));
  }

  return isEqual(prevProps, nextProps);
});

export {abortController, cancelTokenErrorMessage}
export default MemoizedFormManager;
